import { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, Eye, Download, FileText, FileImage, File as FileIcon, Loader2 } from "lucide-react";

const isImage = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => ct === "application/pdf";
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function Viewer({ att, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let url;
    api.get(`/attachments/${att.id}/download`, { responseType: "blob" })
      .then((r) => {
        url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
        setBlobUrl(url);
      })
      .catch(() => setError("Failed to load file"));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id, att.content_type]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-4" data-testid="attachment-viewer-modal">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            {isPdf(att.content_type) ? <FileText className="h-4 w-4 text-rose-500" /> : <FileImage className="h-4 w-4 text-blue-500" />}
            {att.filename}
            <span className="text-[10px] font-mono text-muted-foreground font-normal">{fmtSize(att.size)}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-accent/30 rounded-md overflow-hidden flex items-center justify-center">
          {error && <p className="text-sm text-rose-500">{error}</p>}
          {!error && !blobUrl && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
          {blobUrl && isPdf(att.content_type) && (
            <iframe src={blobUrl} title={att.filename} className="w-full h-full border-0" data-testid="pdf-viewer-frame" />
          )}
          {blobUrl && isImage(att.content_type) && (
            <img src={blobUrl} alt={att.filename} className="max-w-full max-h-full object-contain" data-testid="image-viewer-img" />
          )}
          {blobUrl && !isPdf(att.content_type) && !isImage(att.content_type) && (
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">No inline preview for this file type.</p>
              <a href={blobUrl} download={att.filename}>
                <Button size="sm" data-testid="viewer-download-btn"><Download className="h-4 w-4 mr-1" /> Download</Button>
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AttachmentsPanel({ entityType, entityId, compact = false }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const inputRef = useRef(null);

  const load = () =>
    api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => { load(); }, [entityType, entityId]); // eslint-disable-line

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      const form = new FormData();
      form.append("file", file);
      form.append("entity_type", entityType);
      form.append("entity_id", entityId);
      try {
        await api.post("/attachments", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) { toast.error(`${file.name}: ${apiError(err)}`); }
    }
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
    load();
    setUploading(false);
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete ${att.filename}?`)) return;
    try { await api.delete(`/attachments/${att.id}`); toast.success("Attachment deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openDownload = async (att) => {
    const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
    const a = document.createElement("a");
    a.href = url; a.download = att.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const icon = (ct) => isPdf(ct) ? <FileText className="h-4 w-4 text-rose-500 shrink-0" />
    : isImage(ct) ? <FileImage className="h-4 w-4 text-blue-500 shrink-0" />
    : <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />;

  return (
    <div className="space-y-2" data-testid="attachments-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <Paperclip className="h-3 w-3" /> Attachments ({files.length})
        </p>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={upload} data-testid="attachment-file-input"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.svg,.txt,.md,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.step,.stl,.dxf" />
        <Button size="sm" variant="outline" data-testid="attachment-upload-btn"
          onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </div>
      {files.length > 0 && (
        <div className={`border border-border rounded-md divide-y divide-border ${compact ? "" : "bg-card/50"}`}>
          {files.map((f) => (
            <div key={f.id} className="px-3 py-2 flex items-center gap-2.5" data-testid="attachment-item">
              {icon(f.content_type)}
              <button className="min-w-0 flex-1 text-left group" data-testid="attachment-name-btn"
                onClick={() => (isPdf(f.content_type) || isImage(f.content_type)) ? setViewing(f) : openDownload(f)}>
                <p className="text-sm truncate group-hover:text-primary transition-colors">{f.filename}</p>
                <p className="text-[10px] font-mono text-muted-foreground">
                  {fmtSize(f.size)} · {f.uploaded_by_name} · {new Date(f.created_at).toLocaleDateString()}
                </p>
              </button>
              {(isPdf(f.content_type) || isImage(f.content_type)) && (
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="attachment-view-btn" onClick={() => setViewing(f)} title="View">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="attachment-download-btn" onClick={() => openDownload(f)} title="Download">
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="attachment-delete-btn" onClick={() => remove(f)} title="Delete">
                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {viewing && <Viewer att={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
